import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { t, font, Button, Badge } from "@realtyflows/ui";
import { getContractTemplate } from "../api";

const TAG_TYPES = [
  { key: "signature", label: "Signature", icon: "S", color: "#16a34a", width: 160, height: 32 },
  { key: "date", label: "Date", icon: "D", color: "#2563eb", width: 100, height: 24 },
  { key: "initials", label: "Initials", icon: "I", color: "#8b5cf6", width: 60, height: 24 },
  { key: "text", label: "Text Field", icon: "T", color: "#f59e0b", width: 140, height: 24 },
  { key: "name", label: "Full Name", icon: "N", color: "#06b6d4", width: 140, height: 24 },
];

const SIGNER_COLORS = {
  1: { bg: "#dcfce7", border: "#16a34a", text: "#16a34a", label: "Signer 1" },
  2: { bg: "#dbeafe", border: "#2563eb", text: "#2563eb", label: "Signer 2" },
};

function loadFolders() {
  try { return JSON.parse(localStorage.getItem("rf_contract_folders") || "[]"); } catch { return []; }
}
function saveFolders(folders) {
  localStorage.setItem("rf_contract_folders", JSON.stringify(folders));
}

export default function ESignPrepare({ user }) {
  const { folderId, formEntryId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState({});
  const [labels, setLabels] = useState([]);
  const [activeTool, setActiveTool] = useState(null); // { type, signer }
  const [activeSigner, setActiveSigner] = useState(1);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [toast, setToast] = useState(null);

  // Load template and saved labels
  useEffect(() => {
    const templateId = searchParams.get("templateId");
    if (!templateId) { navigate("/contracts"); return; }

    getContractTemplate(templateId)
      .then(tmpl => {
        setTemplate(tmpl);
        // Load field values from draft
        const initial = {};
        (tmpl.placeholders || []).forEach(p => { initial[p.key] = p.default || ""; });
        try {
          const draft = localStorage.getItem(`form_draft_${templateId}`);
          if (draft) Object.assign(initial, JSON.parse(draft));
        } catch {}
        setFields(initial);

        // Load saved esign labels
        const folders = loadFolders();
        const folder = folders.find(f => f.id === folderId);
        const formEntry = folder?.forms?.find(f => f.id === formEntryId);
        if (formEntry?.esign_labels) setLabels(formEntry.esign_labels);
      })
      .catch(() => navigate("/contracts"))
      .finally(() => setLoading(false));
  }, [folderId, formEntryId, searchParams, navigate]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const showToast = (msg, variant = "success") => setToast({ msg, variant });

  // Save labels to folder
  const handleSave = useCallback(() => {
    const folders = loadFolders();
    const folderIdx = folders.findIndex(f => f.id === folderId);
    if (folderIdx === -1) return;
    const formIdx = folders[folderIdx].forms?.findIndex(f => f.id === formEntryId);
    if (formIdx === -1) return;
    folders[folderIdx].forms[formIdx].esign_labels = labels;
    folders[folderIdx].forms[formIdx].status = labels.length > 0 ? "ready" : "filled";
    saveFolders(folders);
    showToast(`${labels.length} label(s) saved`);
  }, [labels, folderId, formEntryId]);

  // Place a label on click
  const handleCanvasClick = (e) => {
    if (!activeTool || dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top + canvasRef.current.scrollTop;

    const tagType = TAG_TYPES.find(t => t.key === activeTool);
    if (!tagType) return;

    const newLabel = {
      id: Date.now().toString(),
      type: activeTool,
      signer: activeSigner,
      x: Math.max(0, x - tagType.width / 2),
      y: Math.max(0, y - tagType.height / 2),
      width: tagType.width,
      height: tagType.height,
    };

    setLabels(prev => [...prev, newLabel]);
    setSelectedLabel(newLabel.id);
  };

  // Start dragging a label
  const handleLabelMouseDown = (e, label) => {
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    setDragging(label.id);
    setDragOffset({
      x: e.clientX - rect.left - label.x,
      y: e.clientY - rect.top + canvasRef.current.scrollTop - label.y,
    });
    setSelectedLabel(label.id);
  };

  // Move label while dragging
  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = e.clientX - rect.left - dragOffset.x;
      const y = e.clientY - rect.top + canvasRef.current.scrollTop - dragOffset.y;
      setLabels(prev => prev.map(l => l.id === dragging ? { ...l, x: Math.max(0, x), y: Math.max(0, y) } : l));
    };
    const handleUp = () => setDragging(null);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => { window.removeEventListener("mousemove", handleMove); window.removeEventListener("mouseup", handleUp); };
  }, [dragging, dragOffset]);

  const handleDeleteLabel = (id) => {
    setLabels(prev => prev.filter(l => l.id !== id));
    if (selectedLabel === id) setSelectedLabel(null);
  };

  const handleDeleteAll = () => { setLabels([]); setSelectedLabel(null); };

  // Build simple preview HTML from template
  const buildPreviewHtml = () => {
    if (!template?.content?.sections) return "";
    const sections = template.content.sections;
    let html = `<div style="font-family: Georgia, serif; font-size: 11pt; color: #222; padding: 40px 50px; line-height: 1.7;">`;
    html += `<h1 style="text-align:center; font-size:16pt; letter-spacing:1px; margin-bottom:4px;">${template.content.title || template.name}</h1>`;
    html += `<div style="text-align:center; font-size:10pt; color:#888; margin-bottom:24px;">State of ${template.state || "Illinois"}</div>`;

    for (const section of sections) {
      if (section.id === "signatures") continue;
      if (section.title) html += `<h2 style="font-size:12pt; margin:20px 0 6px; border-bottom:1px solid #ccc; padding-bottom:3px;">${section.title}</h2>`;
      let content = (section.content || "")
        .replace(/<strong>/g, "<b>").replace(/<\/strong>/g, "</b>")
        .replace(/\{\{(\w+)\}\}/g, (_, key) => {
          const val = fields[key];
          const p = (template.placeholders || []).find(pl => pl.key === key);
          return `<span style="border-bottom:2px solid #a8c8e8; background:#dce8f5; padding:0 4px; font-weight:600; color:#0a2540;">${val || p?.label || key}</span>`;
        });
      html += `<p style="margin:6px 0; text-align:justify;">${content}</p>`;
    }

    // Signature lines
    html += `<div style="margin-top:40px; border-top:2px solid #222; padding-top:20px;">`;
    html += `<div style="display:flex; gap:40px; margin-bottom:30px;">`;
    for (let i = 0; i < 2; i++) {
      html += `<div style="flex:1;"><div style="border-bottom:2px solid #222; height:30px; margin-bottom:4px;"></div><div style="font-size:9pt; color:#666;">Signature</div></div>`;
      html += `<div style="width:100px;"><div style="border-bottom:2px solid #222; height:30px; margin-bottom:4px;"></div><div style="font-size:9pt; color:#666;">Date</div></div>`;
    }
    html += `</div></div>`;
    html += `</div>`;
    return html;
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: t.textMuted }}>Loading...</div>;
  if (!template) return null;

  const signerCounts = { 1: labels.filter(l => l.signer === 1).length, 2: labels.filter(l => l.signer === 2).length };

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", display: "flex", flexDirection: "column", height: "calc(100vh - 140px)" }}>
      {/* ── Top Bar ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 0", marginBottom: 10, borderBottom: `1px solid ${t.border}`,
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => navigate(`/contracts/folders/${folderId}`)} style={{
            background: "none", border: "none", color: t.textSecondary, fontSize: 13, cursor: "pointer", fontFamily: font.body, fontWeight: 600,
          }}>
            &larr; Back to Folder
          </button>
          <span style={{ color: t.border }}>|</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
            Prepare: {template.name}
          </span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Button variant="outline" size="sm" onClick={handleDeleteAll} disabled={labels.length === 0}>Clear All</Button>
          <Button variant="primary" size="sm" onClick={handleSave}>
            Save Labels ({labels.length})
          </Button>
        </div>
      </div>

      {/* ── Main Content: Toolbar + Canvas ── */}
      <div style={{ display: "flex", flex: 1, gap: 0, overflow: "hidden" }}>
        {/* ── Left: Toolbar ── */}
        <div style={{
          width: 200, background: t.surface, borderRight: `1px solid ${t.border}`,
          padding: 14, display: "flex", flexDirection: "column", gap: 14, overflowY: "auto", flexShrink: 0,
        }}>
          {/* Signer selector */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, marginBottom: 6, fontFamily: "monospace", letterSpacing: 0.5 }}>ASSIGN TO</div>
            {[1, 2].map(s => (
              <button key={s} onClick={() => setActiveSigner(s)} style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%",
                padding: "8px 10px", borderRadius: 6, marginBottom: 4, cursor: "pointer",
                background: activeSigner === s ? SIGNER_COLORS[s].bg : "transparent",
                border: activeSigner === s ? `2px solid ${SIGNER_COLORS[s].border}` : `1px solid ${t.border}`,
                transition: "all 0.15s",
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: "50%",
                  background: SIGNER_COLORS[s].border, color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 700,
                }}>{s}</div>
                <span style={{ fontSize: 11, fontWeight: 600, color: activeSigner === s ? SIGNER_COLORS[s].text : t.textSecondary, fontFamily: font.body }}>
                  {SIGNER_COLORS[s].label}
                </span>
                <Badge variant="default" style={{ marginLeft: "auto", fontSize: 9 }}>{signerCounts[s]}</Badge>
              </button>
            ))}
          </div>

          {/* Tag tools */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, marginBottom: 6, fontFamily: "monospace", letterSpacing: 0.5 }}>SIGNER ACTIONS</div>
            {TAG_TYPES.map(tag => (
              <button key={tag.key} onClick={() => setActiveTool(activeTool === tag.key ? null : tag.key)} style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%",
                padding: "8px 10px", borderRadius: 6, marginBottom: 4, cursor: "pointer",
                background: activeTool === tag.key ? `${tag.color}15` : "transparent",
                border: activeTool === tag.key ? `2px solid ${tag.color}` : `1px solid ${t.border}`,
                transition: "all 0.15s",
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: 4,
                  background: tag.color, color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700,
                }}>{tag.icon}</div>
                <span style={{ fontSize: 11, fontWeight: 600, color: activeTool === tag.key ? tag.color : t.textPrimary, fontFamily: font.body }}>
                  {tag.label}
                </span>
              </button>
            ))}
          </div>

          {/* Instructions */}
          <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body, lineHeight: 1.6, padding: "10px 0", borderTop: `1px solid ${t.border}` }}>
            <strong>How to use:</strong><br />
            1. Select a signer (1 or 2)<br />
            2. Click a tag type above<br />
            3. Click on the document to place it<br />
            4. Drag to reposition<br />
            5. Click tag to select, press Delete to remove
          </div>

          {/* Placed labels list */}
          {labels.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, marginBottom: 6, fontFamily: "monospace", letterSpacing: 0.5 }}>
                PLACED ({labels.length})
              </div>
              {labels.map(label => {
                const tag = TAG_TYPES.find(t => t.key === label.type);
                const signer = SIGNER_COLORS[label.signer];
                return (
                  <div key={label.id} onClick={() => setSelectedLabel(label.id)} style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "4px 8px",
                    borderRadius: 4, marginBottom: 2, cursor: "pointer", fontSize: 10,
                    background: selectedLabel === label.id ? signer.bg : "transparent",
                    border: selectedLabel === label.id ? `1px solid ${signer.border}` : "1px solid transparent",
                  }}>
                    <div style={{ width: 14, height: 14, borderRadius: 2, background: tag?.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700 }}>
                      {tag?.icon}
                    </div>
                    <span style={{ flex: 1, fontFamily: font.body, color: t.textPrimary }}>{tag?.label} · S{label.signer}</span>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteLabel(label.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, padding: 0, fontSize: 12 }}>
                      &times;
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Right: Document Canvas ── */}
        <div
          ref={canvasRef}
          onClick={handleCanvasClick}
          style={{
            flex: 1, overflowY: "auto", background: "#e8e8e8", padding: 20,
            cursor: activeTool ? "crosshair" : "default",
            position: "relative",
          }}
        >
          {/* Document preview */}
          <div style={{
            background: "#fff", maxWidth: 800, margin: "0 auto",
            boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
            position: "relative", minHeight: 1000,
          }}>
            <div dangerouslySetInnerHTML={{ __html: buildPreviewHtml() }} />

            {/* Placed labels overlay */}
            {labels.map(label => {
              const tag = TAG_TYPES.find(t => t.key === label.type);
              const signer = SIGNER_COLORS[label.signer];
              const isSelected = selectedLabel === label.id;
              return (
                <div
                  key={label.id}
                  onMouseDown={(e) => handleLabelMouseDown(e, label)}
                  onClick={(e) => { e.stopPropagation(); setSelectedLabel(label.id); }}
                  onKeyDown={(e) => { if (e.key === "Delete" || e.key === "Backspace") handleDeleteLabel(label.id); }}
                  tabIndex={0}
                  style={{
                    position: "absolute",
                    left: label.x, top: label.y,
                    width: label.width, height: label.height,
                    background: `${signer.bg}e0`,
                    border: `2px ${isSelected ? "solid" : "dashed"} ${signer.border}`,
                    borderRadius: 4,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                    cursor: "grab", userSelect: "none",
                    boxShadow: isSelected ? `0 0 0 2px ${signer.border}40` : "none",
                    zIndex: isSelected ? 10 : 5,
                    outline: "none",
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 2,
                    background: tag?.color, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 9, fontWeight: 700, flexShrink: 0,
                  }}>{tag?.icon}</div>
                  <span style={{ fontSize: 9, fontWeight: 600, color: signer.text, fontFamily: font.body, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {tag?.label} · S{label.signer}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: toast.variant === "error" ? "#fee2e2" : "#dcfce7",
          color: toast.variant === "error" ? "#dc2626" : "#16a34a",
          padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600,
          fontFamily: font.body, boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 9999,
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
